import React, { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";
import { featuredProducts } from "../../content/store/products";
import type { LicenseType } from "../../types/store";

const ProductDetail: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [selectedLicense, setSelectedLicense] = useState<LicenseType>("PERSONAL");
    const [quantity, setQuantity] = useState(1);
    const [isAdding, setIsAdding] = useState(false);

    const product = useMemo(() => {
        return featuredProducts.find((p) => p.id === productId);
    }, [productId]);

    if (!product) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
                <h1 className="text-2xl font-semibold mb-4">Ürün Bulunamadı</h1>
                <p className="text-white/60 mb-6">Aradığınız ürün mevcut değil veya kaldırılmış olabilir.</p>
                <Link
                    to="/store"
                    className="px-6 py-3 bg-white/10 border border-white/20 rounded-full hover:bg-white/15 transition"
                >
                    Mağazaya Dön
                </Link>
            </div>
        );
    }

    const isDigital = product.type === "TEXTURE" || product.type === "STL";
    const licenseMultiplier = selectedLicense === "COMMERCIAL" ? 2.5 : 1;
    const totalPrice = product.price * licenseMultiplier * quantity;

    const handleAddToCart = () => {
        setIsAdding(true);
        addItem({
            product,
            quantity,
            license: selectedLicense,
            isDigital
        });

        setTimeout(() => {
            setIsAdding(false);
        }, 1000);
    };

    return (
        <div className="py-8">
            {/* Breadcrumb */}
            <nav className="mb-8 text-sm">
                <ol className="flex items-center gap-2 text-white/50">
                    <li>
                        <Link to="/store" className="hover:text-white transition">Mağaza</Link>
                    </li>
                    <li>/</li>
                    <li className="text-white/80">{product.name}</li>
                </ol>
            </nav>

            <div className="grid lg:grid-cols-2 gap-12">
                {/* Product Image */}
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-white/5">
                    <img
                        src={product.thumbnailUrl}
                        alt={product.name}
                        className="w-full h-full object-cover"
                    />
                    {product.type && (
                        <span className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur rounded-full text-xs font-medium uppercase tracking-wider">
                            {product.type}
                        </span>
                    )}
                </div>

                {/* Product Info */}
                <div className="flex flex-col">
                    <div className="flex flex-wrap gap-2 mb-4">
                        {product.tags?.map((tag) => (
                            <span
                                key={tag}
                                className="px-2 py-1 bg-white/10 rounded text-xs font-medium text-white/70"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>

                    <h1 className="text-3xl lg:text-4xl font-bold mb-4">{product.name}</h1>

                    <p className="text-white/70 text-lg leading-relaxed mb-6">
                        {product.description}
                    </p>

                    {/* Meta Info */}
                    {product.meta && (
                        <div className="flex flex-wrap gap-4 mb-6 text-sm">
                            {product.meta.texturesCount && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                    <span className="text-white/50">Texture:</span>
                                    <span className="font-medium">{product.meta.texturesCount} adet</span>
                                </div>
                            )}
                            {product.meta.resolution && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                    <span className="text-white/50">Çözünürlük:</span>
                                    <span className="font-medium">{product.meta.resolution}</span>
                                </div>
                            )}
                            {product.meta.stlCount && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                    <span className="text-white/50">STL:</span>
                                    <span className="font-medium">{product.meta.stlCount} dosya</span>
                                </div>
                            )}
                            {product.meta.printTimeEstimateHours && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                                    <span className="text-white/50">Baskı Süresi:</span>
                                    <span className="font-medium">~{product.meta.printTimeEstimateHours} saat</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* License Selection (for digital products) */}
                    {isDigital && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-3 text-white/70">Lisans Tipi</label>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setSelectedLicense("PERSONAL")}
                                    className={`flex-1 py-3 px-4 rounded-xl border transition ${selectedLicense === "PERSONAL"
                                            ? "border-purple-500 bg-purple-500/20 text-white"
                                            : "border-white/10 bg-white/5 text-white/60 hover:border-white/20"
                                        }`}
                                >
                                    <div className="font-medium">Kişisel</div>
                                    <div className="text-sm opacity-70">Ticari olmayan kullanım</div>
                                </button>
                                <button
                                    onClick={() => setSelectedLicense("COMMERCIAL")}
                                    className={`flex-1 py-3 px-4 rounded-xl border transition ${selectedLicense === "COMMERCIAL"
                                            ? "border-purple-500 bg-purple-500/20 text-white"
                                            : "border-white/10 bg-white/5 text-white/60 hover:border-white/20"
                                        }`}
                                >
                                    <div className="font-medium">Ticari</div>
                                    <div className="text-sm opacity-70">Ticari projelerde kullanım</div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Quantity (for physical products) */}
                    {!isDigital && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-3 text-white/70">Adet</label>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/15 transition"
                                >
                                    -
                                </button>
                                <span className="w-12 text-center text-lg font-medium">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/15 transition"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Price & Add to Cart */}
                    <div className="mt-auto pt-6 border-t border-white/10">
                        <div className="flex items-end justify-between mb-4">
                            <div>
                                <span className="text-sm text-white/50">Toplam</span>
                                <div className="text-3xl font-bold">${totalPrice.toFixed(2)}</div>
                            </div>
                            {isDigital && (
                                <span className="text-sm text-green-400 flex items-center gap-1">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    Anında İndirme
                                </span>
                            )}
                        </div>

                        <button
                            onClick={handleAddToCart}
                            disabled={isAdding}
                            className={`w-full py-4 rounded-xl font-semibold text-lg transition ${isAdding
                                    ? "bg-green-500 text-white"
                                    : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                                }`}
                        >
                            {isAdding ? "✓ Sepete Eklendi" : "Sepete Ekle"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Related Products */}
            <section className="mt-16">
                <h2 className="text-2xl font-bold mb-6">Benzer Ürünler</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {featuredProducts
                        .filter((p) => p.id !== product.id && p.type === product.type)
                        .slice(0, 4)
                        .map((relatedProduct) => (
                            <Link
                                key={relatedProduct.id}
                                to={`/store/product/${relatedProduct.id}`}
                                className="group relative aspect-square rounded-xl overflow-hidden bg-white/5"
                            >
                                <img
                                    src={relatedProduct.thumbnailUrl}
                                    alt={relatedProduct.name}
                                    className="w-full h-full object-cover transition group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-4">
                                    <span className="text-sm font-medium">{relatedProduct.name}</span>
                                    <span className="text-sm text-white/70">${relatedProduct.price}</span>
                                </div>
                            </Link>
                        ))}
                </div>
            </section>
        </div>
    );
};

export default ProductDetail;
